import React, { createContext, useContext, useEffect, useState } from "react";
import { AuthService } from "../api/authService";
import Loader from "../components/loader";
import { User } from "../types/user.d";
import { createPortal } from "react-dom";
import useSessionTimers from "../hooks/useSessionTimers";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
} from "@mui/material";

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [inactive, setInactive] = useState(false);
  const [isLoggedOut, setIsLoggedOut] = useState(false);
  const [countdown, setCountdown] = useState(120);

  useEffect(() => {
    const initializeAuth = async () => {
      if (isLoggedOut) return;

      try {
        const user = await AuthService.getCurrentUser();
        setUser(user);
        setIsAuthenticated(true);
      } catch {
        setUser(null);
        setIsAuthenticated(false);
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
  }, [isLoggedOut]);

  const login = async (username: string, password: string) => {
    await AuthService.login(username, password);
    const user = await AuthService.getCurrentUser();
    setUser(user);
    setIsAuthenticated(true);
    setIsLoggedOut(false);
  };

  const logout = async () => {
    try {
      await AuthService.logout();
    } catch {}
    setUser(null);
    setIsAuthenticated(false);
    setIsLoggedOut(true);
    document.cookie = "access_token=; Max-Age=0; path=/;";
    document.cookie = "refresh_token=; Max-Age=0; path=/;";
    window.location.href = "/";
  };

  useSessionTimers(isAuthenticated, logout, setInactive);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (inactive) {
      setCountdown(120);
      interval = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [inactive]);

  useEffect(() => {
    if (inactive && countdown === 0) {
      logout();
    }
  }, [countdown, inactive, logout]);

  if (loading) return <Loader />;

  return (
    <AuthContext.Provider
      value={{ user, isAuthenticated, login, logout, loading }}
    >
      {children}

      {inactive &&
        createPortal(
          <Dialog
            open
            onClose={() => setInactive(false)}
            aria-labelledby="inactive-dialog-title"
            maxWidth="xs"
            fullWidth
          >
            <DialogTitle id="inactive-dialog-title">
              Inactivité détectée
            </DialogTitle>
            <DialogContent>
              <Typography variant="body1" mb={2}>
                Vous serez déconnecté automatiquement dans{" "}
                <strong>{countdown}</strong> secondes pour cause d'inactivité.
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button
                variant="contained"
                color="primary"
                onClick={() => setInactive(false)}
              >
                Rester connecté
              </Button>
            </DialogActions>
          </Dialog>,
          document.body
        )}
    </AuthContext.Provider>
  );
};
